import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchProperties } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import PropertyCard from "@/components/PropertyCard";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [purpose, setPurpose] = useState(params.get("purpose") || "all");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [city, setCity] = useState(params.get("city") || "");
  const [bhk, setBhk] = useState(params.get("bhk") || "all");
  const [search, setSearch] = useState(params.get("search") || "");

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (purpose !== "all") q.purpose = purpose;
    if (category !== "all") q.category = category;
    if (city) q.city = city;
    if (bhk !== "all") q.bhk = Number(bhk);
    if (search) q.search = search;
    fetchProperties(q).then((data) => { setProperties(data); setLoading(false); }).catch(() => setLoading(false));
    const newParams = {};
    Object.entries(q).forEach(([k, v]) => (newParams[k] = String(v)));
    setParams(newParams);
    // eslint-disable-next-line
  }, [purpose, category, city, bhk, search]);

  return (
    <div className="pt-28 pb-24 bg-brand-ivory">
      <SEO title="Property Listings — Indore & Pan India" description="Browse verified property listings — villas, plots, apartments, offices, warehouses & commercial spaces across Indore and Madhya Pradesh." path="/properties" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="mb-8">
            <div className="divider-gold">Discover</div>
            <h1 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy">Property Listings</h1>
            <p className="mt-2 text-brand-charcoal/70">Explore our curated portfolio across categories, cities & budgets.</p>
          </div>
        </Reveal>

        {/* Filters */}
        <div className="bg-white border border-brand-gold/20 p-4 mb-8 grid grid-cols-1 md:grid-cols-6 gap-3">
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger data-testid="filter-purpose" className="rounded-sm"><SelectValue placeholder="Purpose" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Purpose</SelectItem>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
              <SelectItem value="rent">Rent</SelectItem>
              <SelectItem value="lease">Lease</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="filter-category" className="rounded-sm"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" data-testid="filter-city" className="rounded-sm" />
          <Select value={bhk} onValueChange={setBhk}>
            <SelectTrigger data-testid="filter-bhk" className="rounded-sm"><SelectValue placeholder="BHK" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BHK</SelectItem>
              <SelectItem value="1">1 BHK</SelectItem>
              <SelectItem value="2">2 BHK</SelectItem>
              <SelectItem value="3">3 BHK</SelectItem>
              <SelectItem value="4">4 BHK</SelectItem>
              <SelectItem value="5">5+ BHK</SelectItem>
            </SelectContent>
          </Select>
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/50" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, location..." data-testid="filter-search" className="pl-9 rounded-sm" />
          </div>
        </div>

        <div className="text-sm text-brand-charcoal/70 mb-6" data-testid="results-count">
          {loading ? "Loading…" : `${properties.length} propert${properties.length === 1 ? "y" : "ies"} found`}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-96 bg-white border border-brand-gold/10 animate-pulse" />)}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-24 bg-white border border-brand-gold/20">
            <div className="text-brand-gold mb-3"><SlidersHorizontal className="inline" /></div>
            <div className="font-serif text-2xl text-brand-navy">No properties match your filters</div>
            <p className="text-sm text-brand-charcoal/60 mt-2">Try adjusting your filters or reset to see all listings.</p>
            <Button data-testid="reset-filters" onClick={() => { setPurpose("all"); setCategory("all"); setCity(""); setBhk("all"); setSearch(""); }} className="mt-4 rounded-sm bg-brand-navy text-brand-gold">Reset Filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p, i) => <Reveal key={p.id} delay={i * 40}><PropertyCard p={p} /></Reveal>)}
          </div>
        )}
      </div>
    </div>
  );
}
